import React, { useState } from 'react';
import type { User } from '../types';
import {
  Building2,
  UserCircle,
  LayoutDashboard,
  Users,
  Network,
  Bell,
  MessageSquare,
  Shield,
  UserCheck,
  ClipboardList,
  Menu,
  X
} from 'lucide-react';
import type { AdminViewType } from '../App';
import { Button } from './Common';

interface LayoutProps {
  children: React.ReactNode;
  user: User | null;
  activeOrgName?: string;
  activeSubTeamNames?: string;
  onProfileClick: () => void;
  currentAdminView?: AdminViewType;
  onAdminViewChange?: (view: AdminViewType) => void;
  onToggleSubTeams?: (enabled: boolean) => void;
}

export const Layout: React.FC<LayoutProps> = ({ 
  children, 
  user, 
  activeOrgName, 
  activeSubTeamNames,
  onProfileClick,
  currentAdminView,
  onAdminViewChange,
  onToggleSubTeams
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const NavItem = ({
    id,
    label,
    icon: Icon,
    active
  }: {
    id: AdminViewType,
    label: string,
    icon: any,
    active: boolean
  }) => (
    <button
      onClick={() => {
        onAdminViewChange && onAdminViewChange(id);
        setIsMobileMenuOpen(false);
      }}
      className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors border-l-4 ${
        active 
          ? 'bg-aero-800 border-green-500 text-white' 
          : 'border-transparent text-aero-300 hover:bg-aero-800/50 hover:text-white'
      }`}
    >
      <Icon className={`w-5 h-5 ${active ? 'text-green-500' : 'text-aero-400'}`} />
      <span>{label}</span>
    </button>
  );

  return (
    <div className="flex min-h-screen bg-aero-50">
      {/* Mobile backdrop */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Left Sidebar */}
      <aside className={`w-64 bg-aero-900 text-white flex-shrink-0 flex flex-col shadow-xl fixed md:static inset-y-0 left-0 z-40 transform transition-transform duration-200 ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
        <div className="h-16 flex items-center justify-between gap-2 px-6 border-b border-aero-800 bg-aero-900">
           <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center text-white font-bold text-xl shadow-lg transform skew-x-[-10deg]">
                 <span className="transform skew-x-[10deg]">S</span>
              </div>
              <span className="font-bold text-xl tracking-tight text-white">SoloReady</span>
           </div>
           <button
             className="md:hidden p-1 text-aero-300 hover:text-white"
             onClick={() => setIsMobileMenuOpen(false)}
             aria-label="Close menu"
           >
              <X className="w-5 h-5" />
           </button>
        </div>

        <nav className="flex-1 py-6 space-y-1 overflow-y-auto">
          {onAdminViewChange && (
            <>
              <NavItem id="dashboard" label="Dashboard" icon={LayoutDashboard} active={currentAdminView === 'dashboard'} />
              <NavItem id="orgs" label="Organizations" icon={Building2} active={currentAdminView === 'orgs'} />
              
              <div className="pt-4 pb-2 px-6 text-xs font-bold text-aero-500 uppercase tracking-wider">
                  People
              </div>
              <NavItem id="admins" label="Admins" icon={Shield} active={currentAdminView === 'admins'} />
              <NavItem id="candidates" label="Candidates" icon={Users} active={currentAdminView === 'candidates'} />
              
              <div className="pt-4 pb-2 px-6 text-xs font-bold text-aero-500 uppercase tracking-wider">
                  Competency
              </div>
              <NavItem id="pathways" label="Pathways" icon={Network} active={currentAdminView === 'pathways'} />
              <NavItem id="ojt" label="OJT Scheduler" icon={ClipboardList} active={currentAdminView === 'ojt'} />
              
              <div className="pt-4 pb-2 px-6 text-xs font-bold text-aero-500 uppercase tracking-wider">
                  System
              </div>
              <NavItem id="notifications" label="Notifications" icon={Bell} active={currentAdminView === 'notifications'} />
              <NavItem id="messaging" label="Esendex Messaging" icon={MessageSquare} active={currentAdminView === 'messaging'} />
            </>
          )}
        </nav>

        {/* User / Logout Area at Bottom Sidebar */}
        <div className="p-4 border-t border-aero-800">
            <button onClick={onProfileClick} className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-aero-800 transition-colors">
                <div className="w-8 h-8 rounded-full bg-aero-700 flex items-center justify-center text-white">
                    <UserCircle className="w-5 h-5" />
                </div>
                <div className="flex flex-col items-start overflow-hidden">
                    <span className="text-sm font-medium text-white truncate w-full text-left">{user?.name}</span>
                    <span className="text-[10px] text-aero-400 uppercase">ADMIN</span>
                </div>
            </button>
        </div>
      </aside>

      {/* Main Content Wrapper */}
      <div className="flex-1 flex flex-col min-w-0 overflow-x-auto bg-aero-50">
        
        {/* Top Header Bar */}
        <header className="h-16 bg-aero-900 text-white flex items-center justify-between px-6 shadow-md z-10 flex-shrink-0">
             {/* Left side of header (Breadcrumb or context) */}
             <div className="flex items-center gap-4 flex-1 mr-4">
                 <button
                     className="md:hidden p-2 -ml-2 text-aero-300 hover:text-white"
                     onClick={() => setIsMobileMenuOpen(true)}
                     aria-label="Open menu"
                 >
                     <Menu className="w-6 h-6" />
                 </button>
                 {activeOrgName && (
                     <div className="flex items-center gap-3">
                         <div className="flex items-center text-sm font-medium bg-aero-800 px-4 py-1.5 rounded-full border border-aero-700 shadow-inner max-w-full overflow-hidden">
                             <span className="text-white whitespace-nowrap">{activeOrgName}</span>
                             {activeSubTeamNames && (
                                 <span className="text-aero-300 ml-3 truncate border-l border-aero-600 pl-3">
                                     {activeSubTeamNames}
                                 </span>
                             )}
                         </div>
                         {onToggleSubTeams && (
                             <label className="flex items-center gap-2 text-xs text-aero-300 cursor-pointer hover:text-white transition-colors">
                                 <span className="whitespace-nowrap">Include Sub teams</span>
                                 <button
                                     type="button"
                                     role="switch"
                                     aria-checked={user?.activeOrgIncludesSubTeams || false}
                                     onClick={() => onToggleSubTeams(!(user?.activeOrgIncludesSubTeams || false))}
                                     className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-aero-900 ${
                                         user?.activeOrgIncludesSubTeams ? 'bg-green-500' : 'bg-aero-700'
                                     }`}
                                 >
                                     <span
                                         className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                             user?.activeOrgIncludesSubTeams ? 'translate-x-4' : 'translate-x-0.5'
                                         }`}
                                     />
                                 </button>
                             </label>
                         )}
                     </div>
                 )}
             </div>

             {/* Right side Actions */}
             <div className="flex items-center gap-3">
                 <button className="p-2 text-aero-400 hover:text-white transition-colors relative">
                     <Bell className="w-5 h-5" />
                     <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border border-aero-900"></span>
                 </button>
             </div>
        </header>

        {/* Main Page Content */}
        <main className="flex-1 overflow-y-auto p-6 scroll-smooth bg-aero-50 relative">
             <div className="max-w-7xl mx-auto h-full pb-8">
                 {children}
             </div>
             {/* Footer with build timestamp */}
             <footer className="fixed bottom-0 right-0 pr-6 pb-2 pointer-events-none">
                 <span className="text-aero-400" style={{ fontSize: '8px' }}>Build: {__BUILD_TIME__}</span>
             </footer>
        </main>
      </div>
    </div>
  );
};